import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AgoraRTC, { 
  IAgoraRTCClient, 
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack 
} from 'agora-rtc-sdk-ng';
import { api } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { AgoraVideoPlayer } from '../components/AgoraVideoPlayer.tsx';
import { 
  Video, VideoOff, Mic, MicOff, PhoneOff, Users, 
  Plus, ArrowLeft, Ticket, Lock, Play
} from 'lucide-react';
import { toast } from 'sonner';

interface MeetupRoom {
  meetup_id: string;
  event_id: string;
  host_id: string;
  host_name: string;
  title: string;
  description?: string;
  room_code: string;
  participant_count: number;
  max_participants: number;
  status: string;
}

export default function VirtualRoom() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [event, setEvent] = useState<any>(null);
  const [meetupRooms, setMeetupRooms] = useState<MeetupRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<MeetupRoom | null>(null);
  const [hasTicket, setHasTicket] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoined, setIsJoined] = useState(false);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  
  // Agora state
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [localVideoTrack, setLocalVideoTrack] = useState<ICameraVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<IMicrophoneAudioTrack | null>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  
  // Room creation form
  const [roomForm, setRoomForm] = useState({
    title: '',
    description: '',
    max_participants: 10
  });

  useEffect(() => {
    if (eventId) {
      loadData();
    }
    return () => {
      leaveRoom();
    };
  }, [eventId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Load event details
      const eventData = await api.getEvent(eventId!);
      setEvent(eventData);
      
      // Check if user is host/creator
      const isEventHost = eventData.creator_id === user?.user_id;
      setIsHost(isEventHost);
      
      // Try to load meetup rooms (will fail if no ticket)
      try {
        const rooms = await api.getEventMeetupRooms(eventId!);
        setMeetupRooms(rooms);
        setHasTicket(true);
        
        // Check if we should auto-join a specific meetup
        const meetupId = searchParams.get('meetup');
        if (meetupId) {
          const room = rooms.find((r: MeetupRoom) => r.meetup_id === meetupId);
          if (room) {
            joinMeetupRoom(room);
          }
        }
      } catch (error: any) {
        if (error.message?.includes('ticket holders')) {
          setHasTicket(false);
        }
      }
      
    } catch (error: any) {
      toast.error(error.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const createMeetupRoom = async () => {
    if (!roomForm.title) {
      toast.error('Please enter a room title');
      return;
    }

    try {
      const result = await api.createMeetupRoom({
        event_id: eventId!,
        title: roomForm.title,
        description: roomForm.description,
        max_participants: roomForm.max_participants
      });
      
      toast.success('Room created! Starting video...');
      setShowCreateRoom(false);
      
      // Reload rooms and join the new one
      const rooms = await api.getEventMeetupRooms(eventId!);
      setMeetupRooms(rooms);
      
      const newRoom = rooms.find((r: MeetupRoom) => r.meetup_id === result.meetup_id);
      if (newRoom) {
        joinMeetupRoom(newRoom);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create room');
    }
  };

  const joinMeetupRoom = async (room: MeetupRoom) => {
    try {
      const joinResult = await api.joinMeetupRoom(room.meetup_id);
      setCurrentRoom(room);
      setIsHost(joinResult.is_host);
      
      if (joinResult.token && joinResult.appId) {
        // Initialize Agora
        await initializeAgora(joinResult.appId, room.room_code, joinResult.token, joinResult.uid);
      } else {
        toast.error('Video chat unavailable - Agora not configured');
      }
      
    } catch (error: any) {
      toast.error(error.message || 'Failed to join room');
    }
  };

  const initializeAgora = async (appId: string, channel: string, token: string, uid: number) => {
    try {
      const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      clientRef.current = client;
      
      // Set up event handlers
      client.on('user-published', async (remoteUser, mediaType) => {
        await client.subscribe(remoteUser, mediaType);
        if (mediaType === 'video') {
          setRemoteUsers(prev => [...prev.filter(u => u.uid !== remoteUser.uid), remoteUser]);
        }
        if (mediaType === 'audio') {
          remoteUser.audioTrack?.play();
        }
      });
      
      client.on('user-unpublished', (remoteUser, mediaType) => {
        if (mediaType === 'video') {
          setRemoteUsers(prev => prev.filter(u => u.uid !== remoteUser.uid));
        }
      });
      
      client.on('user-left', (remoteUser) => {
        setRemoteUsers(prev => prev.filter(u => u.uid !== remoteUser.uid));
      });
      
      // Join channel
      await client.join(appId, channel, token, uid);
      
      // Create and publish local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
      setLocalAudioTrack(audioTrack);
      setLocalVideoTrack(videoTrack);
      await client.publish([audioTrack, videoTrack]);
      
      setIsJoined(true);
      
    } catch (error) {
      console.error('Agora initialization error:', error);
      toast.error('Failed to start video chat');
    }
  };

  const leaveRoom = async () => {
    if (localVideoTrack) {
      localVideoTrack.stop();
      localVideoTrack.close();
      setLocalVideoTrack(null);
    }
    if (localAudioTrack) {
      localAudioTrack.stop();
      localAudioTrack.close();
      setLocalAudioTrack(null);
    }
    if (clientRef.current) {
      await clientRef.current.leave();
      clientRef.current = null;
    }
    
    if (currentRoom) {
      try {
        await api.leaveMeetupRoom(currentRoom.meetup_id);
      } catch (error) {
        console.error('Error leaving room:', error);
      }
    }
    
    setRemoteUsers([]);
    setIsJoined(false);
    setCurrentRoom(null);
  };

  const toggleVideo = async () => {
    if (localVideoTrack) {
      await localVideoTrack.setEnabled(!isVideoEnabled);
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const toggleAudio = async () => {
    if (localAudioTrack) {
      await localAudioTrack.setEnabled(!isAudioEnabled);
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  const endRoom = async () => {
    if (currentRoom && isHost) {
      try {
        await api.endMeetupRoom(currentRoom.meetup_id);
        toast.success('Room ended');
      } catch (error) {
        console.error('Error ending room:', error);
      }
    }
    await leaveRoom();
    loadData();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // In a video call
  if (isJoined && currentRoom) {
    return (
      <div className="space-y-4 pb-20 md:pb-8">
        <Card className="glass border-white/10">
          <CardContent className="p-0">
            {/* Main video area */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 p-2 min-h-[400px]">
              {/* Local video */}
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                {localVideoTrack ? (
                  <AgoraVideoPlayer videoTrack={localVideoTrack} className="w-full h-full" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <VideoOff className="h-12 w-12 text-gray-600" />
                  </div>
                )}
                <Badge className="absolute bottom-2 left-2 bg-primary">
                  You {isHost && '(Host)'}
                </Badge>
              </div>
              
              {/* Remote videos */}
              {remoteUsers.map((remoteUser) => (
                <div key={remoteUser.uid} className="relative aspect-video bg-black rounded-lg overflow-hidden">
                  {remoteUser.videoTrack ? (
                    <AgoraVideoPlayer videoTrack={remoteUser.videoTrack} className="w-full h-full" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="h-12 w-12 text-gray-600" />
                    </div>
                  )}
                  <Badge className="absolute bottom-2 left-2">User {remoteUser.uid}</Badge>
                </div>
              ))}
            </div>
            
            {/* Controls */}
            <div className="p-4 border-t border-white/10 flex items-center justify-between">
              <div>
                <h3 className="font-bold">{currentRoom.title}</h3>
                <p className="text-sm text-gray-400">
                  {remoteUsers.length + 1} participants • Room: {currentRoom.room_code}
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  onClick={toggleAudio}
                  variant={isAudioEnabled ? 'outline' : 'destructive'}
                  size="icon"
                >
                  {isAudioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                </Button>
                <Button
                  onClick={toggleVideo}
                  variant={isVideoEnabled ? 'outline' : 'destructive'}
                  size="icon"
                >
                  {isVideoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                </Button>
                <Button onClick={endRoom} variant="destructive">
                  <PhoneOff className="h-4 w-4 mr-2" />
                  {isHost ? 'End Room' : 'Leave'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No ticket - show purchase prompt
  if (!hasTicket && !isHost) {
    return (
      <div className="space-y-8 pb-20 md:pb-8">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        <Card className="glass border-white/10 max-w-lg mx-auto text-center p-8">
          <Lock className="h-16 w-16 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold mb-2">Ticket Required</h2>
          <p className="text-gray-400 mb-6">
            Virtual meetup rooms are exclusive to ticket holders. Purchase a ticket to join video sessions with artists and other fans.
          </p>
          {event && (
            <div className="mb-6 p-4 rounded-lg glass">
              <h3 className="font-bold">{event.title}</h3>
              <p className="text-sm text-gray-400">{event.venue}</p>
            </div>
          )}
          <Button onClick={() => navigate(`/event/${eventId}`)} className="shadow-glow">
            <Ticket className="h-4 w-4 mr-2" />
            Get Tickets
          </Button>
        </Card>
      </div>
    );
  }

  // Room list view
  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold">
              {event?.title || 'Virtual Rooms'}
            </h1>
            <p className="text-gray-400">Connect with artists and fans</p>
          </div>
        </div>
        {isHost && (
          <Button onClick={() => setShowCreateRoom(true)} className="shadow-glow">
            <Plus className="h-4 w-4 mr-2" />
            Create Room
          </Button>
        )}
      </div>

      {/* Create Room Form */}
      {showCreateRoom && (
        <Card className="glass border-primary/30">
          <CardHeader>
            <CardTitle>Create Meetup Room</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Room Title</Label>
              <Input
                value={roomForm.title}
                onChange={(e) => setRoomForm({ ...roomForm, title: e.target.value })}
                placeholder="e.g., Meet & Greet, Q&A Session"
                className="glass border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={roomForm.description}
                onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })}
                placeholder="What will you discuss?"
                className="glass border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Max Participants</Label>
              <Input
                type="number"
                value={roomForm.max_participants}
                onChange={(e) => setRoomForm({ ...roomForm, max_participants: parseInt(e.target.value) })}
                min={2}
                max={50}
                className="glass border-white/10 w-32"
              />
            </div>
            
            <div className="flex space-x-3">
              <Button onClick={createMeetupRoom} className="shadow-glow">
                Create & Start
              </Button>
              <Button variant="outline" onClick={() => setShowCreateRoom(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Rooms */}
      <div className="space-y-4">
        <h2 className="text-xl font-heading font-bold">Active Rooms</h2>
        
        {meetupRooms.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {meetupRooms.map((room) => (
              <Card key={room.meetup_id} className="glass border-white/10 hover:border-white/20 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-lg">{room.title}</h3>
                      <p className="text-sm text-gray-400">Hosted by {room.host_name}</p>
                    </div>
                    <Badge variant={room.status === 'active' ? 'default' : 'secondary'}>
                      {room.participant_count}/{room.max_participants}
                    </Badge>
                  </div>
                  
                  {room.description && (
                    <p className="text-gray-400 text-sm mb-4">{room.description}</p>
                  )}
                  
                  <Button 
                    onClick={() => joinMeetupRoom(room)} 
                    className="w-full shadow-glow"
                    disabled={room.participant_count >= room.max_participants}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {room.participant_count >= room.max_participants ? 'Room Full' : 'Join Room'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass border-white/10 p-12 text-center">
            <Video className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-2">No active rooms</p>
            <p className="text-gray-500">
              {isHost 
                ? 'Create a room to start connecting with your fans!' 
                : 'Check back later for live meetup sessions with artists.'}
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
